<template>
  <v-card link :to="route" class="mb-4 d-flex flex-column" height="360">
    <BackgroundImage
      data-private
      class="flex-grow-1"
      :size="IMAGE_SIZES.CARD"
      :mediaObject="rev.image"
      :defaultImage="defaultImage"
    >
      <div class="d-flex align-start default-image-gradient">
        <div
          class="d-flex flex-column justify-space-between px-3 pt-3 pb-1"
          style="height: 100%; width: 100%;"
        >
          <div class="d-flex justify-space-between align-center">
            <RevStatus :rev="rev" showValidationIcons />
            <v-tooltip top>
              <template v-slot:activator="{ on }" v-if="isHighlighted(rev)">
                <v-icon
                  v-if="isHighlighted(rev)"
                  color="amber lighten-2"
                  class="ms-2"
                  v-on="on"
                  >mdi-star</v-icon
                >
              </template>
              {{ $t('rev.status.highlighted') }}
            </v-tooltip>
          </div>
          <p class="white--text overline mb-0">
            {{ $t(typeTag) }}
          </p>
        </div>
      </div>
    </BackgroundImage>

    <v-card-text class="ma-0 px-4 py-4 flex-shrink-1">
      <ChipAvatar small :account="rev.owner" />

      <p class="ma-0 pa-0 mt-3 text-body-2">
        {{ rev.name }}
      </p>
      <v-divider class="ma-0 pa-0 my-3"></v-divider>
      <v-card-actions class="ma-0 pa-0 d-flex justify-space-between">
        <p class="ma-0 pa-0 text-caption grey--text">{{ createdAt }}</p>
        <div class="d-flex align-center">
          <v-icon v-if="hasLiked" @click.prevent="unlike" color="blue"
            >mdi-heart</v-icon
          >
          <v-icon v-else @click.prevent="like">mdi-heart-outline</v-icon>
          <span class="ma-0 pa-0 ms-1 mt-1 caption">{{ likesTotal }}</span>
        </div>
      </v-card-actions>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import _ from 'lodash'
import { IMAGE_SIZES } from 'rever-media-sdk'

import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import revMixin from '@/mixins/revMixin'
import defaultImage from '@/assets/images/camera_no.png'
import RevStatus from '../Rev/RevStatus.vue'
import BackgroundImage from '@/components/BackgroundImage/BackgroundImage.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'

export default {
  name: 'RevCard',

  props: {
    rev: {
      required: true,
      type: Object,
    },
  },

  mixins: [mixin, trackerMixin, revMixin],

  components: {
    RevStatus,
    BackgroundImage,
    ChipAvatar,
  },

  data() {
    return {
      defaultImage,
      hasBeenValidated: false,
      localLikedValue: null,
      localLikesTotal: null,
      localRevPoints: null,
      IMAGE_SIZES,
    }
  },

  computed: {
    ownerPhoto() {
      return this.rev?.owner?.photo
    },

    ownerId() {
      return this.rev?.owner?._id
    },

    isCompleted() {
      return this.rev.revState === 'finished'
    },

    isValidatedForImplementation() {
      return _.get(
        this.rev.metadata,
        'validations__implementation__isValidated',
        false
      )
    },

    likesTotal() {
      if (this.localLikesTotal) return this.localLikesTotal
      return this.rev?.totalLikes ?? 0
    },

    hasLiked() {
      if (this.localLikedValue) return this.localLikedValue
      return this.rev?.likedByMe ?? false
    },

    revPoints() {
      const points = this.localRevPoints ?? this.rev?.points

      return this.$t('common.pointWithCount', { count: points })
    },

    createdAt() {
      if (
        utils.dates.calculateDifferenceInDays(this.rev.createdAt, new Date()) <
        7
      ) {
        return utils.dates.humanDate(this.rev.createdAt)
      }

      if (!utils.dates.isFromTheSameYear(this.rev.createdAt)) {
        return utils.dates.formatDate(this.rev.createdAt, 'MMM D, YYYY')
      }

      return utils.dates.formatDate(this.rev.createdAt, 'MMMM D')
    },

    route() {
      if (this.rev.type === 'problemSolving') {
        return `/problem-solving/${this.rev.organization._id}/${this.rev._id}`
      }
      if (this.rev.type === 'quickAction') {
        return `/quick-action/${this.rev.organization._id}/${this.rev._id}`
      }
      return `/rev/${this.rev.organization._id}/${this.rev._id}`
    },

    typeTag() {
      const revType = this.rev.type
      const customName =
        this.rev?.organization?.customNamings[revType]?.singular ?? ''

      return !customName
        ? this.rev.type
          ? `common.${_.snakeCase(this.rev.type)}_one`
          : 'common.improvement_idea_one'
        : customName
    },

    organizationId() {
      return this.$store.getters.organizationId
    },
  },

  methods: {
    ...mapActions({
      likeItem: 'likeItem',
      unlikeItem: 'unlikeItem',
      getRevById: 'getRevById',
    }),

    goToRev() {
      this.triggerRevViewEvent()
      this.navigateTo(this.route)
    },

    triggerRevViewEvent() {
      this.trackView(this.rev.metadata)
    },

    async fetchRev() {
      try {
        const rev = await this.getRevById({
          organizationId: this.organizationId,
          revId: this.rev._id,
          revType: this.rev.type,
        })
        this.localLikesTotal = rev.totalLikes
        this.localLikedValue = rev.likedByMe
        this.localRevPoints = rev.points
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async like() {
      try {
        await this.likeItem({
          organizationId: this.organizationId,
          revId: this.rev._id,
          revType: this.rev.type,
        })

        await this.fetchRev()
        this.trackLike(this.rev.metadata, this.rev.type, true, 'success')
      } catch (err) {
        this.trackLike(this.rev.metadata, this.rev.type, true, 'fail', err)
      }
    },

    async unlike() {
      try {
        await this.unlikeItem({
          organizationId: this.organizationId,
          revId: this.rev._id,
          revType: this.rev.type,
        })

        await this.fetchRev()
        this.trackLike(this.rev.metadata, this.rev.type, false, 'success')
      } catch (err) {
        this.trackLike(this.rev.metadata, this.rev.type, false, 'fail', err)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.default-image-gradient {
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba($color-shadow, 0.03) 0%,
    rgba($color-shadow, 0.8) 100%
  );
}
</style>
